import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useApp } from "@/hooks/use-app-context";
import { usePlatformStats } from "@/hooks/use-platform-stats";
import { canAccessContributorWorkspace, homePathForRole } from "@/lib/auth/roles";
import { scrollToSection } from "@/components/layout/header/visitor-nav";
import { publicHomeCopy } from "./public-home-copy";
import {
  collectionCountries,
  displayNameMap,
  heroLanguageNames,
  homeFaq,
  languageTiles,
  leaderRows,
} from "./public-home-data";
import { useActiveLanguages, usePublicFaq, useWeeklyLeaders } from "./use-public-home";
import { HomeHero } from "./HomeHero";
import { PlatformFigures } from "./PlatformFigures";
import { ContributeWays, HowItWorks } from "./HowAndWays";
import { LanguageTiles, WeeklyLeaders } from "./LanguagesAndLeaders";
import { CollectionMap, FinalCta, HomeFaq } from "./MapFaqCta";

/**
 * The public home page: what Yodi Data Hub is, live proof that it works, and
 * one click to sign up. Rendered for visitors on /:lang and for everyone on
 * /:lang/site (a signed-in reader gets a way back to their own space).
 */
export function PublicHome() {
  const { language, isAuthenticated, user } = useApp();
  const { lang = "fr" } = useParams<{ lang: string }>();
  const copy = publicHomeCopy(language);

  const platform = usePlatformStats();
  const langues = useActiveLanguages();
  const leaders = useWeeklyLeaders();
  const faq = usePublicFaq();

  const stats = platform.data;
  const active = useMemo(() => langues.data ?? [], [langues.data]);
  const names = useMemo(() => displayNameMap(active), [active]);
  const tiles = useMemo(
    () => (stats ? languageTiles(stats.languages.byLanguage, names, language) : null),
    [stats, names, language],
  );
  const pills = useMemo(
    () => heroLanguageNames(stats?.languages.byLanguage ?? [], names, language),
    [stats, names, language],
  );
  const places = useMemo(() => (stats ? collectionCountries(stats) : null), [stats]);
  const rows = useMemo(() => (leaders.data ? leaderRows(leaders.data) : undefined), [leaders.data]);
  const questions = useMemo(() => homeFaq(faq.data), [faq.data]);

  // Arriving from a header link on another page (/fr#home-faq-title): bring
  // the section into view once the page has rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const frame = requestAnimationFrame(() => scrollToSection(hash.slice(1), "auto"));
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  const signedIn = isAuthenticated && user;
  const ownSpaceHref = signedIn ? homePathForRole(user.role, lang) : null;
  const isContributor = Boolean(signedIn && canAccessContributorWorkspace(user.role));
  const waysTarget = isContributor ? "task" : ownSpaceHref ? { href: ownSpaceHref } : "signup";

  return (
    <div className="overflow-x-clip bg-background">
      <HomeHero
        copy={copy}
        lang={lang}
        ownSpaceHref={ownSpaceHref}
        recordings={stats ? stats.audio.total : null}
        isLoading={platform.isLoading}
        languageNames={pills}
      />
      <PlatformFigures
        copy={copy}
        lang={lang}
        stats={stats}
        isLoading={platform.isLoading}
        isError={platform.isError}
        onRetry={() => void platform.refetch()}
      />
      <HowItWorks copy={copy} />
      <ContributeWays copy={copy} lang={lang} target={waysTarget} />
      <LanguageTiles copy={copy} lang={lang} tiles={tiles} isLoading={platform.isLoading} />
      <WeeklyLeaders
        copy={copy}
        lang={lang}
        rows={rows}
        isLoading={leaders.isLoading}
        isError={leaders.isError}
        onRetry={() => void leaders.refetch()}
      />
      <CollectionMap copy={copy} places={places} isLoading={platform.isLoading} />
      <HomeFaq copy={copy} items={questions} />
      <FinalCta
        copy={copy}
        href={ownSpaceHref ?? `/${lang}/signup`}
        label={ownSpaceHref ? copy.hero.ownSpace : copy.final.cta}
      />
    </div>
  );
}
